import { useState, type FormEvent } from "react";
import { Navigate } from "react-router-dom";
import { AlertCircle, Eye, EyeOff } from "lucide-react";
import { ROLE_LABELS, type Role } from "@bruhat/shared";
import { useAuthStore } from "@/store/auth.store";
import { Button, Field, Input } from "@/components/ui";

/** Every account apps/api/prisma/seed.ts creates — keep the two in step. */
const SEED_PASSWORD = "Bruhat@2026";

/**
 * Ordered by how much the account can do, because the usual reason to open this
 * list is "why is every button disabled?". Saying so on the row answers it
 * before it becomes a bug report.
 */
const SEED_ACCOUNTS: Array<{ email: string; role: Role; note?: string }> = [
  { email: "admin@bruhat.test", role: "COMPANY_ADMIN", note: "full access" },
  { email: "finance@bruhat.test", role: "FINANCE_MANAGER" },
  { email: "accounts@bruhat.test", role: "ACCOUNTANT" },
  { email: "projects@bruhat.test", role: "PROJECT_MANAGER" },
  { email: "purchase@bruhat.test", role: "PURCHASE_MANAGER" },
  { email: "stores@bruhat.test", role: "STORE_MANAGER" },
  { email: "director@bruhat.test", role: "DIRECTOR", note: "view + approve" },
  { email: "auditor@bruhat.test", role: "AUDITOR", note: "read-only" },
];

export function LoginPage() {
  const status = useAuthStore((s) => s.status);
  const login = useAuthStore((s) => s.login);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  /*
    Signing in goes to "/", not to the accountant's dashboard.

    This was the main way a Simple-Mode user ended up looking at debits and
    credits: the landing redirect that picks a home by mode only runs on "/",
    and signing in skipped straight past it. Sending everyone here keeps one
    definition of home rather than a second one that drifts from it.
  */
  if (status === "authenticated") return <Navigate to="/" replace />;

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email.trim(), password);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Sign-in failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-full">
      {/* Brand panel — hidden on small screens where it would only cost space. */}
      <div className="relative hidden w-1/2 flex-col justify-between bg-brand-500 p-10 text-white lg:flex">
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/15 text-base font-bold">
            B
          </div>
          <span className="text-lg font-semibold">Bruhat ERP</span>
        </div>

        <div className="max-w-md">
          <h1 className="text-3xl font-semibold leading-tight tracking-tight">
            A complete financial operating system for real-estate developers.
          </h1>
          <p className="mt-4 text-sm leading-relaxed text-brand-100">
            Tally-grade double-entry accounting, project costing, receivables,
            contractor billing, inventory and statutory compliance — in one
            auditable ledger.
          </p>

          <div className="mt-8 grid grid-cols-2 gap-4 text-sm">
            {[
              [
                "Double-entry engine",
                "Every rupee balanced, every entry traceable",
              ],
              ["Project costing", "Budget vs committed vs actual, per sqft"],
              ["Receivables", "Schedules, demands, receipts and ageing"],
              ["GST & TDS", "Rates in data, reviewable before posting"],
            ].map(([title, body]) => (
              <div key={title}>
                <p className="font-medium text-accent-300">{title}</p>
                <p className="mt-0.5 text-xs text-brand-100">{body}</p>
              </div>
            ))}
          </div>
        </div>

        <p className="text-xs text-brand-200">
          Sales CRM lives separately in WhatCRM. This is the financial source of
          truth.
        </p>
      </div>

      <div className="flex w-full items-center justify-center bg-surface-muted p-6 dark:bg-night lg:w-1/2">
        <div className="w-full max-w-sm">
          <div className="mb-8 flex items-center gap-2.5 lg:hidden">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-500 text-base font-bold text-white">
              B
            </div>
            <span className="text-lg font-semibold text-ink dark:text-slate-100">
              Bruhat ERP
            </span>
          </div>

          <h2 className="text-xl font-semibold text-ink dark:text-slate-100">
            Sign in
          </h2>
          <p className="mt-1 text-sm text-ink-muted dark:text-slate-400">
            Use your company account to continue.
          </p>

          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            <Field label="Email">
              <Input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="you@company.com"
                autoComplete="username"
                required
                autoFocus
              />
            </Field>

            <Field label="Password">
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  required
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1.5 text-ink-subtle hover:text-ink"
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </Field>

            {error && (
              <div className="flex items-start gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <Button
              type="submit"
              loading={submitting}
              className="w-full"
              size="lg"
            >
              Sign in
            </Button>
          </form>

          {import.meta.env.DEV && (
            <div className="mt-6 rounded-lg border border-dashed border-surface-border p-3 dark:border-night-border">
              <p className="text-2xs font-semibold uppercase tracking-wider text-ink-subtle">
                Development seed accounts
              </p>
              <div className="mt-2 space-y-1">
                {SEED_ACCOUNTS.map(({ email: seedEmail, role, note }) => (
                  <button
                    key={seedEmail}
                    type="button"
                    onClick={() => {
                      setEmail(seedEmail);
                      setPassword(SEED_PASSWORD);
                    }}
                    className="flex w-full items-center justify-between gap-2 rounded px-1 py-0.5 text-2xs hover:bg-surface-sunken dark:hover:bg-night-sunken"
                  >
                    <span className="truncate font-mono text-ink-muted dark:text-slate-400">
                      {seedEmail}
                    </span>
                    <span className="shrink-0 text-ink-subtle">
                      {ROLE_LABELS[role]}
                      {note ? ` · ${note}` : ""}
                    </span>
                  </button>
                ))}
              </div>
              <p className="mt-2 border-t border-surface-border pt-2 text-2xs text-ink-subtle dark:border-night-border">
                Click one to fill the form. All use{" "}
                <span className="font-mono">{SEED_PASSWORD}</span>.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
